import { atom, useAtomValue, useSetAtom } from 'jotai'
import type { Atom, PrimitiveAtom } from 'jotai'
import { z } from 'zod'
import { normalizeCardBrand, validateCardExpiration } from '~/lib/reservationInput/card'
import { env$ } from '~/stores/env'
import { token } from '~/styled-system/tokens'
import type { PayjpTokenResponse } from '~/types/payjp'
import { selectedCardStatus$ } from './card'
import { hasCardError, submitCardDisabled } from './lib/checkDisabled'
import type { CardErrors, SelectedCardStatus } from './lib/checkDisabled'
import { scope } from './scope'
import { card$, visitDate$ } from './stateMachine/context'
import { plan$ } from './stateMachine/state'
import type { NewCard, SelectedCard } from './stateMachine/statemachine'

type PayjpInstance = {
  createToken: (element: any, options: any) => Promise<any>
}

const payjpPublicKey$ = atom((get) => {
  return get(env$).PAYJP_SERVICE_PUBLIC_KEY
})

export function usePayjpPublicKey() {
  return useAtomValue(payjpPublicKey$)
}

const payjpObject$ = scope.atom<PayjpInstance | null>(null, 'payjpObject$')

/** payjp.js v2 入力フォーム生成の初期化処理 */
const initPayjpObject$ = atom(
  null,
  async (get, set, payjp: any, cardNumberId: string, cardExpiryId: string, cardCvcId: string) => {
    const elements = payjp.elements()

    // payjp.js v2 の style オブジェクトを使ってスタイルを適用する
    // https://pay.jp/docs/payjs#style%E3%82%AA%E3%83%96%E3%82%B8%E3%82%A7%E3%82%AF%E3%83%88
    const style = {
      base: {
        fontSize: '14px',
        fontWeight: 'normal',
        '::placeholder': {
          color: `${token('colors.not.recommended.ikyu.gray.400')}`,
        },
      },
    }

    const cardNumberElement = elements.create('cardNumber', {
      placeholder: '0000 1111 2222 3333',
      style,
    })
    cardNumberElement.mount(`#${CSS.escape(cardNumberId)}`)

    const cardExpiryElement = elements.create('cardExpiry', {
      placeholder: 'MM / YY',
      style,
    })
    cardExpiryElement.mount(`#${CSS.escape(cardExpiryId)}`)

    const cardCvcElement = elements.create('cardCvc', {
      placeholder: '123',
      style,
    })
    cardCvcElement.mount(`#${CSS.escape(cardCvcId)}`)

    cardNumberElement.on('change', (event: any) => {
      set(setPayjpElementStatus$, event, cardNumberStatus$)
    })
    cardExpiryElement.on('change', (event: any) => {
      set(setPayjpElementStatus$, event, cardExpiryStatus$)
    })
    cardCvcElement.on('change', (event: any) => {
      set(setPayjpElementStatus$, event, cardCvcStatus$)
    })

    set(cardNumberElement$, cardNumberElement)
    set(payjpObject$, payjp)
  }
)

export function useInitPayjpObject() {
  return useSetAtom(initPayjpObject$)
}

type PayjpElementStatus = { type: 'error'; message: string } | { type: 'completed' }

const setPayjpElementStatus$ = atom(
  null,
  (get, set, event: any, statusAtom: PrimitiveAtom<PayjpElementStatus | undefined>) => {
    if (event.error !== null) {
      set(statusAtom, { type: 'error', message: event.error.message })
      return
    }
    if (event.complete) {
      set(statusAtom, { type: 'completed' })
    }
  }
)

const cardNumberElement$ = scope.atom<any>(undefined, 'cardNumberElement$')

const cardNumberStatus$ = scope.atom<PayjpElementStatus | undefined>(undefined, 'cardNumberStatus$')

const cardNumberError$ = atom((get) => {
  const status = get(cardNumberStatus$)
  return status?.type === 'error' ? status.message : undefined
})

export function useCardNumberError() {
  return useAtomValue(cardNumberError$)
}

const cardExpiryStatus$ = scope.atom<PayjpElementStatus | undefined>(undefined, 'cardExpiryStatus$')

const cardExpiryError$ = atom((get) => {
  const status = get(cardExpiryStatus$)
  return status?.type === 'error' ? status.message : undefined
})

export function useCardExpiryError() {
  return useAtomValue(cardExpiryError$)
}

const cardCvcStatus$ = scope.atom<PayjpElementStatus | undefined>(undefined, 'cardCvcStatus$')

const cardCvcError$ = atom((get) => {
  const status = get(cardCvcStatus$)
  return status?.type === 'error' ? status.message : undefined
})

export function useCardCvcError() {
  return useAtomValue(cardCvcError$)
}

const cardHolder$ = scope.atom<string | undefined>(undefined, 'cardHolder$')

export function useCardHolder() {
  return useAtomValue(cardHolder$)
}

const setCardHolder$ = atom(null, (get, set, value: string) => {
  set(cardHolder$, value)
})

export function useSetCardHolder() {
  return useSetAtom(setCardHolder$)
}

const cardHolderError$ = atom((get) => {
  return validateCardHolder(get(cardHolder$))
})

export function useCardHolderError() {
  return useAtomValue(cardHolderError$)
}

const cardHolderStatus$ = atom((get) => {
  if (get(cardHolderError$) !== undefined) {
    return { type: 'error', message: get(cardHolderError$) }
  }
  return { type: 'completed' }
})

const REQUIRED_CARD_HOLDER_MESSAGE = '入力が完了していません。'
const LENGTH_CARD_HOLDER_MESSAGE = 'カード名義は45文字以内で入力してください。'
const INVALID_CARD_HOLDER_MESSAGE =
  'カード名義は半角英数字、スペース、ピリオド、ハイフンのみで入力してください。'

const cardHolderSchema = z
  .string()
  .min(1, { message: REQUIRED_CARD_HOLDER_MESSAGE })
  .max(45, { message: LENGTH_CARD_HOLDER_MESSAGE })
  .regex(/^[a-zA-Z0-9 .-]+$/, {
    message: INVALID_CARD_HOLDER_MESSAGE,
  })

// PAY.JP card object の name プロパティのバリデーションに準拠する
// https://pay.jp/docs/api/#card%E3%82%AA%E3%83%96%E3%82%B8%E3%82%A7%E3%82%AF%E3%83%88
function validateCardHolder(cardHolder?: string) {
  if (cardHolder === undefined) {
    return undefined
  }
  const result = cardHolderSchema.safeParse(cardHolder)
  if (!result.success) {
    return result.error.issues[0].message
  }
  return undefined
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('validateCardHolder', () => {
    expect(validateCardHolder()).toBe(undefined)
    expect(validateCardHolder('TARO IKYU')).toBe(undefined)
    expect(validateCardHolder('taro.ikyu')).toBe(undefined)
    expect(validateCardHolder('taro-ikyu')).toBe(undefined)
    expect(validateCardHolder('')).toBe(REQUIRED_CARD_HOLDER_MESSAGE)
    expect(validateCardHolder('a'.repeat(46))).toBe(LENGTH_CARD_HOLDER_MESSAGE)
    expect(validateCardHolder('taro@ikyu')).toBe(INVALID_CARD_HOLDER_MESSAGE)
    expect(validateCardHolder('いっきゅう たろう')).toBe(INVALID_CARD_HOLDER_MESSAGE)
    expect(validateCardHolder('一休太郎')).toBe(INVALID_CARD_HOLDER_MESSAGE)
  })
}

export const isInputCompleted$ = atom((get) => {
  return (
    get(cardNumberStatus$)?.type === 'completed' &&
    get(cardExpiryStatus$)?.type === 'completed' &&
    get(cardCvcStatus$)?.type === 'completed' &&
    get(cardHolderStatus$)?.type === 'completed'
  )
})
isInputCompleted$.debugLabel = 'isInputCompleted$'

export const errors$: Atom<CardErrors> = atom((get) => {
  return {
    cardNumberError: get(cardNumberError$),
    cardHolderError: get(cardHolderError$),
    securityCodeError: get(cardCvcError$),
    cardExpirationError: get(cardExpiryError$),
  }
})
errors$.debugLabel = 'errors$'

export const userStatus$ = atom((get) => {
  return {
    card: {
      isInputCompleted: get(isInputCompleted$),
      errors: get(errors$),
      selected: get(selectedCardStatus$),
    },
  }
})
userStatus$.debugLabel = 'userStatus$'

/** PAY.JP に新しいカード情報を送信してトークンを生成する処理 */
export const createPayjpNewCardToken$ = atom<null, [], Promise<NewCard>>(null, async (get) => {
  const payjpObject = get(payjpObject$)
  const payjpCardNumberElement = get(cardNumberElement$)
  const cardHolder = get(cardHolder$)
  if (
    payjpObject === null ||
    payjpCardNumberElement === null ||
    !get(isInputCompleted$) ||
    hasCardError(get(errors$))
  ) {
    throw new Error('カード情報の登録に失敗しました。入力内容をご確認の上、再度お試し下さい。')
  }

  // トークンを生成する
  const response: PayjpTokenResponse = await payjpObject.createToken(payjpCardNumberElement, {
    card: { name: cardHolder },
  })
  if ('error' in response) {
    throw new Error(response.error.message)
  }

  const cardBrand = normalizeCardBrand(response.card.brand)
  if (cardBrand === undefined) {
    throw new Error(
      'ご入力のカードブランドはご利用いただけません。入力内容をご確認の上、再度お試し下さい。'
    )
  }

  // カード有効期限のバリデーション
  // カード情報の入力値は payjp.js v2 によりセキュアに処理され、入力時にバリデーションできないため、
  // トークン生成後のレスポンス値を見てバリデーションする
  // 2025年1月の場合: 2025, 1 -> '25', '01' に変換する
  const year = `${response.card.exp_year}`.slice(-2)
  const month = `${response.card.exp_month}`.padStart(2, '0')
  const visitDate = get(visitDate$)
  const cardExpiryError = validateCardExpiration({ year, month, visitDate })
  if (cardExpiryError !== undefined) {
    throw new Error(cardExpiryError)
  }

  return {
    maskedNumber: response.card.last4,
    goodThru: `${month}/${year}`,
    brand: cardBrand,
    holderName: response.card.name,
    token: response.id,
  }
})
createPayjpNewCardToken$.debugLabel = 'createPayjpNewCardToken$'

/** 予約完了ボタンの上に表示するエラーメッセージ */
export const cancelGuardCardErrorMessage$ = atom((get) => {
  return cancelGuardCardErrorMessage(
    get(isInputCompleted$),
    get(errors$),
    get(plan$)?.isCancelGuard,
    get(card$),
    get(selectedCardStatus$)
  )
})
cancelGuardCardErrorMessage$.debugLabel = 'cancelGuardCardErrorMessage$'

function cancelGuardCardErrorMessage(
  isInputCompleted: boolean,
  errors: CardErrors,
  isCancelGuard?: boolean,
  card?: SelectedCard,
  selectedCardStatus?: SelectedCardStatus
) {
  if (!isCancelGuard) {
    return ''
  }
  // 登録済カードがあり、予約で利用するカードが決まっている
  if (card !== undefined) {
    return ''
  }
  // 登録済カードが無い場合は、新規カード情報の入力が必須
  if (submitCardDisabled(isInputCompleted, errors, selectedCardStatus)) {
    return 'カード情報を登録してください。'
  }
  return ''
}

if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('cancelGuardCardErrorMessage', async () => {
    // キャンセルガードプランではない場合
    expect(cancelGuardCardErrorMessage(false, {}, false)).toBe('')
    // 登録済カードがあり、カードが決まっている場合
    expect(
      cancelGuardCardErrorMessage(false, {}, true, { type: 'registered', cardId: 'cardId' })
    ).toBe('')
    // 登録済カードがあり、カードふすまで新規カードを登録した場合
    expect(
      cancelGuardCardErrorMessage(
        true,
        {},
        true,
        {
          type: 'new',
          newCard: {
            maskedNumber: '4242 4242 4242 4242',
            holderName: 'Taro Yamada',
            goodThru: '12/25',
            brand: 'VISA',
            token: 'token',
          },
        },
        { type: 'new' }
      )
    ).toBe('')
    // 登録済カードがなく、新規カード情報の入力が未完了の場合
    expect(
      cancelGuardCardErrorMessage(false, { cardNumberError: '不正な値です' }, true, undefined, {
        type: 'new',
      })
    ).toBe('カード情報を登録してください。')
    // 登録済カードがなく、新規カード情報の入力が完了している場合
    expect(cancelGuardCardErrorMessage(true, {}, true, undefined, { type: 'new' })).toBe('')
  })
}
